import { useTranslation } from 'react-i18next';
import { Alert, Button, Checkbox, FormControlLabel, LinearProgress } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import React, { useMemo, useRef, useState } from 'react';
import {
  useAllIngredients,
  useAllOutOfStockItemsByShopExternalId,
  useAllProducts,
  useRemoveOutOfStock,
} from '../../../apis/products-api';
import styled from 'styled-components';
import { PageContainer } from '../../../shared/Styled/Styled';
import { RestaurantPageHeader } from '../../../shared/RestaurantPageHeader/RestaurantPageHeader';
import { AddCircleOutline } from '@mui/icons-material';
import LoadingIndicator from '../../../shared/LoadingIndicator';
import { useAllRestaurants } from '../../../apis/shops-api';
import { combineOutOfStockItems, groupDuplicateIngredients, OutOfStockItem } from '../OutOfStockUtils';
import { MetaLabel, useExternalShopId, useOutOfStockPermissions } from '../OutOfStockShared';
import { AddOutOfStockItemModal } from './AddOutOfStockItemModal';
import { OutOfStockRow } from './OutOfStockRow';
import { EditOutOfStockItemModal } from './EditOutOfStockItemModal';
import { useCustomSnackBar } from '../../../shared/SnackBar/useCustomSnackBar';
import { getProductsApiError } from '../../../apis/products-api.errors';

export const RestaurantOutOfStockView: React.FC = () => {
  const [checkedIds, setCheckedIds] = useState<Set<number>>(() => new Set());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<OutOfStockItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The state above drives the busy UI; this ref is the actual re-entrancy guard, because two clicks
  // can both read a stale `isSubmitting` before React commits the update and fire the batch twice.
  const isSubmittingRef = useRef(false);

  const { t } = useTranslation();
  const snackBar = useCustomSnackBar();
  const externalShopId = useExternalShopId();

  // isInitialLoading rather than isLoading: the out-of-stock query is disabled until the shop id
  // resolves, and a disabled query reports isLoading forever — which used to spin the page indefinitely.
  const productsQuery = useAllProducts(false);
  const ingredientsQuery = useAllIngredients(false);
  const outOfStockQuery = useAllOutOfStockItemsByShopExternalId(externalShopId);
  const { data: products } = productsQuery;
  const { data: ingredients } = ingredientsQuery;
  const { data: outOfStockItems } = outOfStockQuery;
  const removeOutOfStock = useRemoveOutOfStock();

  // Only feeds the header's meta line, so it is deliberately kept out of the loading gate below: a
  // slow shops query fills in the restaurant name late rather than holding back the whole list.
  const { data: restaurants } = useAllRestaurants();
  const restaurant = restaurants?.find((r) => r.shopExternalId === externalShopId);

  const {
    canView: userAuthorizesToViewOutOfStock,
    canUpdate: userAuthorizesToUpdateOutOfStock,
    isLoading: isPermissionsLoading,
  } = useOutOfStockPermissions();

  const items = useMemo(() => {
    const combinedItems = combineOutOfStockItems(
      outOfStockItems?.outOfStockIngredients,
      outOfStockItems?.outOfStockProducts,
      ingredients,
      products
    );

    return groupDuplicateIngredients(combinedItems, ingredients).sort((a, b) => {
      if (!a.scheduledUntil && !b.scheduledUntil) return 0;
      if (!a.scheduledUntil) return 1;
      if (!b.scheduledUntil) return -1;

      return new Date(a.scheduledUntil).getTime() - new Date(b.scheduledUntil).getTime();
    });
  }, [ingredients, outOfStockItems?.outOfStockIngredients, outOfStockItems?.outOfStockProducts, products]);

  // Only scheduled items can be selected (unscheduled items cannot be restored from this list).
  const selectableItems = useMemo(() => items.filter((item) => item.scheduledUntil), [items]);
  const selectedCount = items.filter((item) => checkedIds.has(item.itemId)).length;
  const hasSelection = selectedCount > 0;
  const allSelectableChecked =
    selectableItems.length > 0 && selectableItems.every((item) => checkedIds.has(item.itemId));

  const toggleItem = (itemId: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setCheckedIds(allSelectableChecked ? new Set() : new Set(selectableItems.map((item) => item.itemId)));
  };

  const handleReturnProducts = async () => {
    const productsToReturnToMenu = items.filter((item) => checkedIds.has(item.itemId));
    if (
      isSubmittingRef.current ||
      !productsToReturnToMenu.length ||
      !userAuthorizesToUpdateOutOfStock ||
      !externalShopId
    )
      return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    const restoreRequests = productsToReturnToMenu.flatMap((p) =>
      p.groupedItemIds.map((itemId) =>
        p.type === 'ingredient' ? { ingredientId: itemId, externalShopId } : { productId: itemId, externalShopId }
      )
    );

    try {
      await Promise.all(restoreRequests.map((request) => removeOutOfStock.mutateAsync(request)));
      setCheckedIds(new Set());
      snackBar.showSuccess(t('Returned to sale'));
    } catch (e) {
      snackBar.showError(getProductsApiError(e).message);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // Four distinct states, where there used to be one boolean that also swallowed the mutations and
  // took the whole page down with them.
  const isInitialLoading =
    isPermissionsLoading ||
    outOfStockQuery.isInitialLoading ||
    ingredientsQuery.isInitialLoading ||
    productsQuery.isInitialLoading;
  const isError = outOfStockQuery.isError || ingredientsQuery.isError || productsQuery.isError;
  // A refetch triggered by one of the mutations: the list is on screen and about to change under the user.
  const isRefreshing = outOfStockQuery.isFetching && !isInitialLoading;

  const handleRetry = () => {
    void outOfStockQuery.refetch();
    void ingredientsQuery.refetch();
    void productsQuery.refetch();
  };

  // The panel shows exactly one of four things; named branches read better than a nested ternary.
  const renderPanelContent = () => {
    if (isInitialLoading) return <LoadingIndicator height="200px" />;

    if (isError) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              {t('Try again')}
            </Button>
          }
        >
          {t('Loading the data failed')}
        </Alert>
      );
    }

    if (items.length === 0) return <EmptyText>{t('This restaurant has no out of stock items')}</EmptyText>;

    return (
      <>
        <SelectionRow>
          <SelectAllLabel
            control={
              <Checkbox
                color="primary"
                checked={allSelectableChecked}
                indeterminate={hasSelection && !allSelectableChecked}
                onChange={toggleAll}
                disabled={selectableItems.length === 0 || isSubmitting}
              />
            }
            label={<MetaLabel>{t('Select all')}</MetaLabel>}
          />
          {hasSelection && <MetaLabel>{t('restaurantOutOfStock.selectedCount', { amount: selectedCount })}</MetaLabel>}
        </SelectionRow>

        <ColumnHeader>
          <MetaLabel>{t('singleRestaurant.outOfStock.name')}</MetaLabel>
          <MetaLabel>{t('singleRestaurant.outOfStock.returns')}</MetaLabel>
        </ColumnHeader>

        {items.map((item) => (
          <OutOfStockRow
            key={item.itemId}
            item={item}
            checked={checkedIds.has(item.itemId)}
            isSubmitting={isSubmitting}
            onToggle={toggleItem}
            onOpen={setEditingItem}
          />
        ))}
      </>
    );
  };

  if (!isPermissionsLoading && !userAuthorizesToViewOutOfStock) return null;

  return (
    <PageContainer>
      {/* The add dialog filters out what is already out of stock, so it needs the list to have arrived. */}
      <RestaurantPageHeader
        title={t('Out of stock ingredients and products')}
        restaurant={restaurant}
        actions={
          <AddButton
            disabled={!userAuthorizesToUpdateOutOfStock || isInitialLoading || isError}
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={() => setIsAddModalOpen(true)}
          >
            {t('Add out-of-stock item')}
          </AddButton>
        }
      />

      <Panel>
        {/* Always mounted so the refresh hint cannot push the list down when it appears. */}
        <LinearProgress sx={{ visibility: isRefreshing ? 'visible' : 'hidden' }} />
        {renderPanelContent()}
      </Panel>
      {hasSelection && (
        <SelectionBar>
          <RestoreButton
            disabled={!userAuthorizesToUpdateOutOfStock}
            loading={isSubmitting}
            variant="contained"
            onClick={handleReturnProducts}
            color="primary"
          >
            {t('Restore selected products')}
          </RestoreButton>
        </SelectionBar>
      )}

      <AddOutOfStockItemModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        existingOutOfStockItems={items}
      />
      <EditOutOfStockItemModal item={editingItem} onClose={() => setEditingItem(null)} />
    </PageContainer>
  );
};

// Full-width thumb target on phones; shrinks to its label once the header becomes a row.
const AddButton = styled(Button)`
  && {
    flex-shrink: 0;
    width: 100%;

    ${({ theme }) => theme.breakpoints.up('sm')} {
      width: auto;
    }
  }
`;

const Panel = styled.div`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: ${({ theme }) => theme.spacing(2)};
  box-sizing: border-box;
`;

const SelectionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
`;

// gap 12px lands the label at 54px — the same offset as a row's name (42px checkbox + 4px Row gap +
// 8px ItemArea padding), so "select all" lines up with the NIMI column.
const SelectAllLabel = styled(FormControlLabel)`
  && {
    margin: 0;
    gap: 12px;
  }
`;

// Padded to sit exactly over the columns it labels: 54px clears the checkbox gutter on the left, and
// 32px on the right accounts for the rows' chevron slot (20px), its gap (4px) and ItemArea padding (8px).
// Hidden on phones, where the rows stack into two lines and no longer form columns to label.
const ColumnHeader = styled.div`
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 32px 6px 54px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    display: flex;
  }
`;

// Sticky inside the centred column rather than fixed to the viewport, so it can never stretch under
// the side drawer. On a short list there is nothing to scroll and it simply sits after the panel.
const SelectionBar = styled.div`
  position: sticky;
  bottom: ${({ theme }) => theme.spacing(2)};
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing(1.5)};
  background-color: ${({ theme }) => theme.palette.background.paper};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const RestoreButton = styled(LoadingButton)`
  && {
    width: 100%;

    ${({ theme }) => theme.breakpoints.up('sm')} {
      width: auto;
    }
  }
`;

const EmptyText = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

export default RestaurantOutOfStockView;
